import { PublicRoutes } from "@/constants/routes";
import { CalendarCheck, CalendarDays, QrCode, Store } from "lucide-react";
import type { LegalDocument } from "./types";

// Regra do payment_window explicada para o lojista, sem termo técnico. Tem de
// andar junto com o cálculo: mudou a regra no backend, muda esta página e o
// changelog.
export const PAYMENT_ATTRIBUTION: LegalDocument = {
  key: "pagamentos",
  path: PublicRoutes.COMO_CONTAMOS_PAGAMENTOS,
  eyebrow: "Transparência",
  title: "Como a Fidexa conta os pagamentos",
  seoTitle: "Como a Fidexa conta os pagamentos recuperados",
  description:
    "Quando um pagamento conta como ajuda da Fidexa: 5 dias após a cobrança, o primeiro dia de pagamento, compra nova, agendamento e Pix da Fidexa, com exemplos.",
  intro:
    "No painel, no relatório de contas pagas e no resultado da cobrança, a Fidexa mostra quanto dinheiro entrou por causa das cobranças que ela enviou. Esta página explica, sem termos técnicos, quando um pagamento entra nessa conta e quando não entra.",
  version: "1.0",
  updatedAt: "2026-10-22",

  highlights: [
    {
      icon: CalendarDays,
      title: "5 dias depois da cobrança",
      text: "Cada mensagem de cobrança abre um prazo de 5 dias. Pagamento fora desse prazo não entra na conta.",
    },
    {
      icon: CalendarCheck,
      title: "Conta o dia do primeiro pagamento",
      text: "Dentro do prazo, vale o primeiro dia em que o cliente pagou, com todas as parcelas pagas nesse dia.",
    },
    {
      icon: Store,
      title: "Dívida antiga, na mesma loja",
      text: "Só conta parcela de compra feita antes da cobrança e paga na loja que cobrou. Compra nova não entra.",
    },
    {
      icon: QrCode,
      title: "Pix da Fidexa sempre conta",
      text: "Parcela paga pelo Pix do Fidexa Pay conta sempre, porque o pagamento aconteceu dentro da Fidexa.",
    },
  ],

  sections: [
    {
      id: "a-regra",
      title: "A regra em poucas palavras",
      summary:
        "Depois de cada cobrança, o cliente tem 5 dias. O primeiro dia em que ele pagar conta inteiro para a Fidexa.",
      blocks: [
        {
          type: "paragraph",
          text: "Quando a Fidexa envia uma mensagem de cobrança para um cliente, começa a contar um prazo de **5 dias**. Se o cliente pagar dentro desse prazo, **tudo o que ele pagar no primeiro dia de pagamento** conta como ajuda da Fidexa, seja uma parcela ou várias, da mesma compra ou de compras diferentes.",
        },
        {
          type: "paragraph",
          text: "Se o cliente voltar à loja em outro dia para pagar mais alguma coisa, sem ter recebido uma nova cobrança, esse segundo pagamento não conta. Entendemos que ele foi à loja por outro motivo, e não por causa da mensagem.",
        },
        {
          type: "callout",
          tone: "info",
          title: "Por que contamos assim",
          text: "Preferimos uma conta mais conservadora: só entra o que a mensagem de cobrança provavelmente causou. Assim o valor que você vê no painel é dinheiro que dá para atribuir à Fidexa com segurança.",
        },
      ],
    },
    {
      id: "passo-a-passo",
      title: "Passo a passo",
      summary:
        "Mensagem enviada, prazo de 5 dias, primeiro dia de pagamento conta, os demais dias não.",
      blocks: [
        {
          type: "list",
          ordered: true,
          items: [
            "A Fidexa envia a mensagem de cobrança ao cliente. O prazo começa a contar **no próprio dia da mensagem**.",
            "O prazo vai até **5 dias depois**. Mensagem no dia 1º, o prazo vai até o dia 6.",
            "O **primeiro dia** em que o cliente paga, dentro do prazo, conta para a Fidexa, com **todas as parcelas** pagas nesse dia.",
            "Só entram parcelas de **compras feitas antes da mensagem**, pagas na **mesma loja** que cobrou. A Fidexa recupera a dívida que já existia.",
            "Pagamentos em **outros dias** do mesmo prazo não contam.",
            "Se a Fidexa enviar **uma nova cobrança**, começa um prazo novo de 5 dias, e a conta recomeça.",
          ],
        },
        {
          type: "paragraph",
          text: "Pagamento feito no mesmo dia da mensagem também conta. A cobrança sai quando a loja abre, então ela chegou antes do pagamento.",
        },
      ],
    },
    {
      id: "exemplo",
      title: "Exemplo: o João",
      summary:
        "Em janeiro a Fidexa ajudou a recuperar R$ 24,00 do João; em fevereiro, R$ 7,00. Os pagamentos de outros dias não entraram.",
      blocks: [
        {
          type: "subheading",
          text: "Janeiro: cobrança no dia 01/01, prazo até 06/01",
        },
        {
          type: "timeline",
          caption: "Pagamentos do João em janeiro",
          items: [
            {
              time: "01/01",
              text: "A Fidexa envia a cobrança. O João tem até 06/01.",
            },
            {
              time: "02/01",
              text: "Paga três parcelas: R$ 5,00, R$ 7,00 e R$ 12,00. **É o primeiro dia de pagamento: as três contam.**",
              tone: "success",
            },
            {
              time: "03/01",
              text: "Paga mais uma parcela de R$ 3,00. **Não conta:** já houve um dia de pagamento nesse prazo e não chegou cobrança nova.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "**Resultado de janeiro:** a Fidexa ajudou a recuperar **R$ 24,00** do João (R$ 5,00 + R$ 7,00 + R$ 12,00).",
        },
        {
          type: "subheading",
          text: "Fevereiro: cobrança no dia 02/02, prazo até 07/02",
        },
        {
          type: "timeline",
          caption: "Pagamentos do João em fevereiro",
          items: [
            {
              time: "02/02",
              text: "A Fidexa envia uma nova cobrança. Começa um prazo novo, até 07/02.",
            },
            {
              time: "07/02",
              text: "Paga duas parcelas: R$ 5,00 e R$ 2,00. **Último dia do prazo e primeiro dia de pagamento: as duas contam.**",
              tone: "success",
            },
            {
              time: "08/02",
              text: "Paga uma parcela de R$ 2,00. **Não conta:** o prazo terminou em 07/02.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "**Resultado de fevereiro:** a Fidexa ajudou a recuperar **R$ 7,00** do João (R$ 5,00 + R$ 2,00).",
        },
      ],
    },
    {
      id: "compra-nova",
      title: "Quando o cliente faz uma compra nova",
      summary:
        "A compra nova não conta para a Fidexa, nem a entrada. O que conta é o pagamento da dívida antiga.",
      blocks: [
        {
          type: "paragraph",
          text: "Muitas vezes o cliente recebe a cobrança, vai até a loja, acerta o que estava atrasado e aproveita para comprar de novo. A Fidexa conta **só o pagamento da dívida que já existia** quando ela enviou a mensagem. A compra nova, inclusive a **entrada**, é venda da loja e não entra na conta da Fidexa.",
        },
        {
          type: "subheading",
          text: "Exemplo: o João compra de novo",
        },
        {
          type: "timeline",
          caption: "O João recebe a cobrança e faz uma compra nova",
          items: [
            {
              time: "10/04",
              text: "A Fidexa envia a cobrança de duas parcelas atrasadas. O João tem até 15/04.",
            },
            {
              time: "11/04",
              text: "Faz uma compra nova no crediário e dá **R$ 50,00 de entrada**. **Não conta:** é uma compra nova, não a dívida que foi cobrada. E o prazo continua aberto.",
            },
            {
              time: "12/04",
              text: "Volta e paga as duas parcelas atrasadas: R$ 40,00 e R$ 60,00. **Contam:** é o primeiro dia em que ele pagou a dívida antiga.",
              tone: "success",
            },
          ],
        },
        {
          type: "paragraph",
          text: "**Resultado:** a Fidexa ajudou a recuperar **R$ 100,00** do João. Os R$ 50,00 de entrada continuam aparecendo no seu sistema como venda da loja.",
        },
        {
          type: "callout",
          tone: "info",
          title: "Pagou o atrasado e comprou no mesmo dia?",
          text: "As parcelas atrasadas pagas nesse dia contam para a Fidexa. A compra nova, não. A cobrança trouxe o cliente de volta, e a venda nova fica com a loja.",
        },
        {
          type: "subheading",
          text: "Renegociação não é pagamento",
        },
        {
          type: "paragraph",
          text: "Quando a loja **renegocia** a dívida e troca as parcelas antigas por novas, nenhum dinheiro entrou ainda. A troca não conta como pagamento. Conta quando o cliente pagar as parcelas novas, seguindo as mesmas regras desta página.",
        },
      ],
    },
    {
      id: "o-que-nao-conta",
      title: "O que não conta",
      summary:
        "Pagamento em outro dia do mesmo prazo, depois dos 5 dias, em outra loja ou sem nenhuma cobrança antes.",
      blocks: [
        {
          type: "table",
          caption: "Situações que não entram na conta da Fidexa",
          columns: [
            { key: "situacao", header: "Situação", primary: true },
            { key: "exemplo", header: "Exemplo" },
          ],
          rows: [
            {
              situacao: "Segundo dia de pagamento no mesmo prazo",
              exemplo: "Cobrança em 01/01, pagou em 02/01 e voltou para pagar em 03/01. O dia 03/01 não conta.",
            },
            {
              situacao: "Depois dos 5 dias",
              exemplo: "Cobrança em 01/01, primeiro pagamento só em 08/01. Não conta.",
            },
            {
              situacao: "Em outra loja",
              exemplo: "A loja A cobrou o João, e ele pagou uma parcela da loja B. Não conta para a loja B.",
            },
            {
              situacao: "Compra nova ou entrada",
              exemplo: "Cobrança em 10/04, o cliente comprou de novo em 11/04 e deu entrada. A entrada não conta.",
            },
            {
              situacao: "Renegociação",
              exemplo: "A loja trocou as parcelas antigas por novas. A troca não é pagamento e não conta.",
            },
            {
              situacao: "Sem cobrança antes",
              exemplo: "O cliente pagou sem ter recebido nenhuma mensagem de cobrança nos 5 dias anteriores. Não conta.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Esses pagamentos continuam no seu sistema e na sua loja normalmente. Eles só não aparecem como ajuda da Fidexa.",
        },
      ],
    },
    {
      id: "agendamento",
      title: "Quando o cliente agenda o pagamento",
      summary:
        "Se a loja agenda uma data com o cliente, o prazo vai até 5 dias depois da data combinada.",
      blocks: [
        {
          type: "paragraph",
          text: "Às vezes o cliente responde a cobrança combinando uma data para pagar, e a loja registra esse **agendamento** na Fidexa. Nesse caso o prazo não termina 5 dias depois da mensagem: ele vai até **5 dias depois da data agendada**.",
        },
        {
          type: "timeline",
          caption: "Exemplo de agendamento",
          items: [
            {
              time: "01/03",
              text: "A Fidexa envia a cobrança para a Maria.",
            },
            {
              time: "02/03",
              text: "A Maria pede para pagar no dia 15. A loja agenda para 15/03. O prazo passa a ir até 20/03.",
            },
            {
              time: "17/03",
              text: "A Maria paga duas parcelas. **Conta:** está dentro dos 5 dias depois da data agendada.",
              tone: "success",
            },
          ],
        },
        {
          type: "paragraph",
          text: "A regra do primeiro dia continua valendo: se a Maria voltar em 18/03 para pagar mais uma parcela, essa não conta.",
        },
        {
          type: "paragraph",
          text: "O agendamento também abre um prazo quando a loja combina a data **sem ter enviado cobrança antes**, por exemplo numa conversa pelo WhatsApp. O prazo vai do dia em que a loja registrou o agendamento até 5 dias depois da data combinada.",
        },
      ],
    },
    {
      id: "pix-da-fidexa",
      title: "Pagamento pelo Pix da Fidexa",
      summary:
        "Parcela paga pelo Pix do Fidexa Pay conta sempre, em qualquer dia.",
      blocks: [
        {
          type: "paragraph",
          text: "Quando o cliente paga pelo **Pix do Fidexa Pay**, gerado pelo WhatsApp da loja, o pagamento aconteceu dentro da Fidexa. Por isso ele **sempre conta**, mesmo fora do prazo de 5 dias e mesmo que não seja o primeiro dia de pagamento.",
        },
        {
          type: "callout",
          tone: "success",
          title: "Exemplo",
          text: "O Pedro não recebeu cobrança neste mês, mas abriu o WhatsApp da loja e pagou uma parcela de R$ 80,00 pelo Pix da Fidexa. Os R$ 80,00 contam como ajuda da Fidexa.",
        },
        {
          type: "paragraph",
          text: "Se o pagamento pelo Pix acontecer dentro do prazo de uma cobrança, ele conta como o **primeiro dia de pagamento** desse prazo. Assim, se o cliente pagar uma parcela pelo Pix no dia 02 e outra no balcão no dia 03, só o dia 02 conta.",
        },
      ],
    },
    {
      id: "onde-ver",
      title: "Onde você vê esses números",
      summary:
        "Valor recebido segue a data do pagamento. Mensagens enviadas e conversão seguem a data da cobrança.",
      blocks: [
        {
          type: "table",
          caption: "Onde cada número aparece e qual data ele usa",
          columns: [
            { key: "numero", header: "Número", primary: true },
            { key: "onde", header: "Onde aparece" },
            { key: "data", header: "Qual data usa" },
          ],
          rows: [
            {
              numero: "Valor recebido, juros e desconto",
              onde: "Painel e relatório de contas pagas",
              data: "O dia do pagamento",
            },
            {
              numero: "Mensagens enviadas",
              onde: "Painel",
              data: "O dia em que a cobrança foi enviada",
            },
            {
              numero: "Conversão",
              onde: "Painel, faixas de cobrança e resultado da cobrança",
              data: "O dia em que a cobrança foi enviada",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Por isso a **conversão dos últimos dias ainda pode subir**. Uma cobrança enviada hoje pode ser paga daqui a alguns dias, e esse pagamento aparece na conversão de hoje quando acontecer. Já o valor recebido entra no dia em que o cliente pagou.",
        },
        {
          type: "paragraph",
          text: "Os números aparecem no painel poucos minutos depois que a baixa das parcelas chega à Fidexa, pela integração ou pela planilha importada.",
        },
      ],
    },
    {
      id: "perguntas-frequentes",
      title: "Perguntas frequentes",
      summary:
        "As dúvidas mais comuns de quem compara o painel da Fidexa com o sistema da loja.",
      blocks: [
        {
          type: "faq",
          items: [
            {
              question:
                "Por que um pagamento que está no meu sistema não aparece como ajuda da Fidexa?",
              answer:
                "Porque nem todo pagamento acontece por causa da cobrança. O pagamento só conta se o cliente recebeu uma mensagem de cobrança da sua loja nos 5 dias anteriores e se foi o primeiro dia em que ele pagou nesse prazo. Os demais pagamentos continuam no seu sistema normalmente.",
            },
            {
              question:
                "O cliente pagou várias parcelas, de compras diferentes, no mesmo dia. Todas contam?",
              answer:
                "Sim. Se for o primeiro dia de pagamento dentro do prazo, **todas as parcelas pagas nesse dia** contam, sejam da mesma compra ou de compras diferentes, desde que as compras sejam anteriores à cobrança.",
            },
            {
              question:
                "A mensagem foi no dia 1º e o cliente pagou no dia 7. Conta?",
              answer:
                "Não. O prazo vai do dia da mensagem até 5 dias depois, então uma mensagem no dia 1º vale até o dia 6. Se a loja tiver registrado um agendamento, o prazo vai até 5 dias depois da data combinada.",
            },
            {
              question:
                "A Fidexa mandou duas cobranças antes de o cliente pagar. Qual vale?",
              answer:
                "Vale a mais recente. Cada nova cobrança começa um prazo novo de 5 dias, e o pagamento conta para a última mensagem que o cliente recebeu antes de pagar.",
            },
            {
              question:
                "O cliente foi cobrado por uma loja e pagou em outra loja da mesma rede. Conta?",
              answer:
                "Não. Conta só o pagamento de parcelas da **mesma loja** que enviou a cobrança. Se a loja A cobrou e o cliente pagou uma parcela da loja B, esse pagamento não entra.",
            },
            {
              question:
                "O cliente veio pagar e aproveitou para comprar de novo. A compra nova conta?",
              answer:
                "Não. A Fidexa conta só a dívida que já existia quando a mensagem foi enviada. As parcelas atrasadas pagas nesse dia contam; a compra nova e a entrada ficam como venda da loja.",
            },
            {
              question:
                "O cliente pagou pelo Pix da Fidexa sem ter recebido cobrança. Conta?",
              answer:
                "Sim. Pagamento pelo Pix do Fidexa Pay sempre conta, porque aconteceu dentro da Fidexa. Se ele acontecer dentro do prazo de uma cobrança, passa a ser o primeiro dia de pagamento desse prazo.",
            },
            {
              question:
                "Por que a conversão dos últimos dias parece baixa?",
              answer:
                "Porque o cliente ainda tem tempo para pagar. A conversão é contada pelo dia em que a cobrança saiu, e quem recebeu a mensagem hoje pode pagar nos próximos 5 dias. O número dos últimos dias vai subindo conforme os pagamentos chegam.",
            },
            {
              question:
                "Quanto tempo depois do pagamento o valor aparece no painel?",
              answer:
                "Poucos minutos depois que a baixa da parcela chega à Fidexa, pela integração com o seu sistema ou pela planilha importada. Se a baixa só for enviada no dia seguinte, o valor aparece no dia seguinte.",
            },
            {
              question:
                "Como confiro, parcela por parcela, o que a Fidexa contou?",
              answer:
                "No painel da Fidexa, em **Relatórios**, gere o relatório de **contas a receber pagas** do período. Ele lista cada parcela que contou como ajuda da Fidexa, com o cliente, a data da cobrança, a data do pagamento e o valor, pronto para comparar com o seu sistema.",
            },
          ],
        },
      ],
    },
    {
      id: "duvidas",
      title: "Ficou alguma dúvida?",
      summary:
        "Fale com o nosso suporte e conte qual cliente ou qual parcela você quer entender.",
      blocks: [
        {
          type: "paragraph",
          text: `Se um pagamento não aparecer como você esperava, envie ao suporte o nome do cliente, a loja e a data do pagamento. A gente mostra, parcela por parcela, por que ele entrou ou não entrou na conta. Os canais estão em [Contato](${PublicRoutes.CONTATO}).`,
        },
      ],
    },
  ],

  hideRelatedDocs: true,
  // Em validação: a regra do texto ainda não é a do painel. Tirar o noindex
  // (e voltar sitemap, llms.txt, LEGAL_NAV e LEGAL_DOCUMENTS) quando a regra
  // nova do ciclo entrar em produção.
  noindex: true,

  changelog: [
    {
      version: "1.0",
      date: "2026-10-22",
      changes: ["Primeira versão da página."],
    },
  ],
};
